import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Eye, EyeOff, ArrowRight } from 'lucide-react';

import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';

import { api } from '@/lib/api';
import { errorMessage } from '@/lib/utils';
import { AuthSplitLayout } from './Login';

export default function ResetPassword() {

  const [params] = useSearchParams();

  const token = params.get('token');

  const navigate = useNavigate();

  const { toast } = useToast();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (password !== confirmPassword) {

      toast({
        title: 'Passwords do not match',
        description: 'Please make sure both passwords are identical.',
        variant: 'destructive',
      });

      return;
    }

    try {

      setLoading(true);

      await api.post('/auth/reset-password', {
        token,
        new_password: password,
      });

      toast({
        title: 'Password Updated',
        description: 'Your password has been reset successfully.',
        variant: 'success',
      });

      navigate('/login');

    } catch (err) {

      toast({
        title: 'Reset Failed',
        description: errorMessage(err.response?.data?.detail, 'Invalid or expired reset link'),
        variant: 'destructive',
      });

    } finally {

      setLoading(false);
    }
  };

  return (
    <AuthSplitLayout mode="customer">

      <div className="lg:hidden mb-10">
        <Logo />
      </div>

      <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-3">
        Password Reset
      </div>

      <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight leading-none">
        Create a new<br />password.
      </h1>

      <p className="mt-3 text-white/50 text-sm">
        Your new password must be secure and different from previous passwords.
      </p>

      <form
        onSubmit={handleSubmit}
        className="mt-10 space-y-5"
      >

        {/* New Password */}

        <Field
          label="New Password"
          htmlFor="password"
          hint="8+ characters, with an uppercase letter, lowercase letter, number, and special character."
        >

          <div className="relative">

            <Input
              id="password"
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) =>
                setPassword(e.target.value)
              }
              placeholder="At least 8 characters"
              required
              minLength={8}
              className="pr-12"
            />

            <button
              type="button"
              onClick={() =>
                setShow((s) => !s)
              }
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-volt"
            >
              {show ? (
                <EyeOff size={16} />
              ) : (
                <Eye size={16} />
              )}
            </button>

          </div>

        </Field>

        {/* Confirm Password */}

        <Field
          label="Confirm Password"
          htmlFor="confirm-password"
        >

          <div className="relative">

            <Input
              id="confirm-password"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) =>
                setConfirmPassword(e.target.value)
              }
              placeholder="••••••••"
              required
              className="pr-12"
            />

            <button
              type="button"
              onClick={() =>
                setShowConfirm((s) => !s)
              }
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-volt"
            >
              {showConfirm ? (
                <EyeOff size={16} />
              ) : (
                <Eye size={16} />
              )}
            </button>

          </div>

        </Field>

        <Button
          type="submit"
          className="w-full"
          disabled={loading}
        >
          {loading ? (
            'Updating Password…'
          ) : (
            <>
              Reset Password
              <ArrowRight size={16} />
            </>
          )}
        </Button>

        <div className="text-center pt-2">

          <Link
            to="/login"
            className="text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-volt"
          >
            ← Back to Login
          </Link>

        </div>

      </form>

    </AuthSplitLayout>
  );
}